import React from 'react';
import { Reducer } from 'redux';
import { AppProps } from 'next/app';
import { createWrapper } from 'next-redux-wrapper';
import thunkMiddleware from 'redux-thunk';
import isServer from '@/Framework/Router/Next/isServer';
import { createContainer, PageRenderingState } from '@/Framework/DI';
import { createStore } from '@/Framework/Bootstrap/createStore';
import HealthCheckPage from '@/Framework/UI/Pages/HealthCheck';

const initialStoreValue = {};

const getWrappedComponent = (reducer, App, useRouterMiddleware) => {
  const container = createContainer(
    isServer() ? PageRenderingState.ServerSideRendering : PageRenderingState.ClientSideRendering,
  );
  const makeStore = createStore({
    container,
    reducer,
    middleware: [
      thunkMiddleware,
    ],
    useRouterMiddleware,
  });

  const store = makeStore(initialStoreValue);

  return createWrapper(makeStore).withRedux((props) => (
    <App
      container={ container }
      store={ store }
      { ...props }
    />
  ));
};

export const wrapper = (reducer: Reducer) => ({
  /**
   * Parameter 'useRouterMiddleware' is a temporary solution for the ability to get rid of
   * connected-next-router package from the DmPortal, Dealroadshow and Users apps.
   * For these apps useRouterMiddleware = false
   * - https://finsight.myjetbrains.com/youtrack/issue/FIN-28064
   * It could be deleted when the apps will be removed redux from usage.
   */
  withRedux: (App, { useRouterMiddleware } = { useRouterMiddleware: true }) => {
    // On Server Side we need to recreate container for each request
    // On Client Side we need to remember container in the closure.

    if (isServer()) {
      return (props) => {
        const Component = getWrappedComponent(reducer, App, useRouterMiddleware);
        return <Component { ...props } />;
      };
    }

    return getWrappedComponent(reducer, App, useRouterMiddleware);
  },
});

export const healthCheck = (App: React.ElementType) => (props: AppProps) => {
  if (props.pageProps.healthcheck) {
    return <HealthCheckPage Component={ props.Component } pageProps={ props.pageProps } />;
  }
  return <App { ...props } />;
};
